/**
 * Tracks which open house events a user has already reserved a spot for.
 * Stored in localStorage per store so the check persists across page reloads.
 *
 * Key format: `booked-events:{storeId}`
 * Value: JSON array of `{ eventId, eventName, bookedAt }`
 */

const STORAGE_KEY_PREFIX = 'booked-events:';

interface BookedEventEntry {
  eventId: string;
  eventName: string;
  bookedAt: string; // ISO date string
}

function getStorageKey(storeId: string): string {
  return `${STORAGE_KEY_PREFIX}${storeId}`;
}

function readEntries(storeId: string): BookedEventEntry[] {
  try {
    const raw = localStorage.getItem(getStorageKey(storeId));
    return raw ? (JSON.parse(raw) as BookedEventEntry[]) : [];
  } catch {
    return [];
  }
}

function writeEntries(storeId: string, entries: BookedEventEntry[]): void {
  try {
    localStorage.setItem(getStorageKey(storeId), JSON.stringify(entries));
  } catch {
    // localStorage may be full or unavailable
  }
}

/** Check if the user already booked a specific event. */
export function isEventBooked(storeId: string, eventId: string): boolean {
  return readEntries(storeId).some(e => e.eventId === eventId);
}

/** Mark an event as booked after successful reservation. */
export function markEventBooked(
  storeId: string,
  eventId: string,
  eventName: string,
): void {
  const entries = readEntries(storeId);
  if (entries.some(e => e.eventId === eventId)) return;
  entries.push({ eventId, eventName, bookedAt: new Date().toISOString() });
  writeEntries(storeId, entries);
}

/** Get the booked entry for a specific event (for displaying info). */
export function getBookedEventEntry(
  storeId: string,
  eventId: string,
): BookedEventEntry | undefined {
  return readEntries(storeId).find(e => e.eventId === eventId);
}
